import { apiNode } from './api'
import { DefaultGetParamsProps } from './types/interfaces'
import {
	ChangeOrganizationDataProps,
	CheckOrganizationExist,
	GetCategoriesDataResponse,
	GetGroupsDataResponse,
	GetOrganizationUsersProps,
	GetPermissionSetsDataResponse,
	GetRolesDataResponse,
	GetUsersDataResponse,
	InviteUserDataProps,
	SetDefaultCategoryDataProps,
} from './types/organization.interfaces'
import { UploadAvatarDataProps } from './types/user.interfaces'

const URL = '2/organization/v2/organization'

export const OrganizationService = {
	// organization actions
	async create({
		data,
	}: {
		data: ChangeOrganizationDataProps
	}) {
		return apiNode.post(`${URL}/create`, data)
	},
	async delete() {
		return apiNode.delete(`${URL}/delete`)
	},
	async updateProfile({
		data,
	}: {
		id?: string
		data: ChangeOrganizationDataProps
	}) {
		return apiNode.patch(`${URL}/update`, data)
	},
	async updateLogo({
		data,
	}: {
		data: UploadAvatarDataProps
		id?: string
	}) {
		const formData = new FormData()
		formData.append('Picture', data.Picture)
		formData.append('Width', String(data.Width))
		formData.append('Height', String(data.Height))
		formData.append('X', String(data.X))
		formData.append('Y', String(data.Y))
		return apiNode.patch(`${URL}/profile/logo`, formData)
	},

	// admin user actions
	async inviteUser({
		data,
	}: {
		data: InviteUserDataProps
		id: string
	}) {
		return apiNode.post(`${URL}/users/invite`, data)
	},
	async reinviteUser({
		data,
	}: {
		data: InviteUserDataProps
		id: string
	}) {
		return apiNode.post(`${URL}/users/${data.userId}/invite`, data)
	},
	async deleteUser({
		userId,
	}: {
		id: string
		userId: string
	}) {
		return apiNode.delete(`${URL}/users/${userId}`)
	},

	// get admin items
	async getRoles({
		id,
		query,
		offset,
		limit = 1000,
	}: DefaultGetParamsProps): Promise<GetRolesDataResponse> {
		const response = await apiNode.get<GetRolesDataResponse>(`${URL}/roles`, {
			params: { id, query, offset, limit },
		})
		return response.data
	},
	async getUsers({
		id,
		query,
		offset,
		limit = 1000,
	}: GetOrganizationUsersProps): Promise<GetUsersDataResponse> {
		const response = await apiNode.get<GetUsersDataResponse>(`${URL}/users`, {
			params: { id, query: query?.trim(), offset, limit },
		})
		return response.data
	},
	async getGroups({
		id,
		query,
		offset,
		limit = 1000,
	}: DefaultGetParamsProps): Promise<GetGroupsDataResponse> {
		const response = await apiNode.get<GetGroupsDataResponse>(`${URL}/groups`, {
			params: { id, query: query?.trim(), offset, limit },
		})
		return response.data
	},
	async getPermissionSets({
		id,
		query,
		offset,
		limit = 1000,
	}: DefaultGetParamsProps): Promise<GetPermissionSetsDataResponse> {
		const response = await apiNode.get<GetPermissionSetsDataResponse>(
			`${URL}/permission-sets`,
			{
				params: { id, query: query?.trim(), offset, limit },
			},
		)
		return response.data
	},
	async getCategories({
		id,
		query,
	}: DefaultGetParamsProps): Promise<GetCategoriesDataResponse> {
		const response = await apiNode.get<GetCategoriesDataResponse>(
			`${URL}/categories`,
			{
				params: { id, query: query?.trim() },
			},
		)
		return response.data
	},

	// change admin items
	async setDefaultCategory({
		id,
		parentId,
	}: SetDefaultCategoryDataProps) {
		return apiNode.put(`${URL}/set-default-category`, { id, parentId })
	},
	async toggleUserStatus({
		value,
		userId,
	}: {
		value: boolean
		userId: string
		id: string
	}) {
		return apiNode.patch(`${URL}/users/${userId}/deactivate`, { value })
	},
	async toggleUserAdminRights({
		value,
		userId,
	}: {
		value: boolean
		userId: string
		id: string
	}) {
		return apiNode.patch(`${URL}/users/${userId}/admin`, { value })
	},
	async checkOrganizationExist({
		domain,
		slug,
	}: CheckOrganizationExist): Promise<unknown> {
		const query = []
		if (domain) query.push(`domain=${domain}`)
		if (slug) query.push(`slug=${slug}`)

		const url = `${URL}/exists?${query.join('&')}`

		const response = await apiNode.get<unknown>(url)
		return response.data
	},
	async checkOrganizationSlug({
		slug,
	}: {
		slug: string
	}): Promise<{ success: boolean; data: boolean }> {
		const response = await apiNode.get<{ success: boolean; data: boolean }>(
			`${URL}/exists?slug=${slug}`,
		)
		return response.data
	},
}
